<template>
  <div class="plans">

    <div class="p-grid p-mt-1">
      <div class="p-col-12 p-xl-5 p-xxl-4">
        <h2 class="p-mb-1">{{ $t('admin.plans.title') }}</h2>
      </div>
      <div class="p-col">
        <div class="p-grid p-jc-center" style="width: 100%;">

          <!-- PLUS / PREMIUM -->
          <div class="p-col-12 p-lg-6 p-xl-12 p-xxl-6" v-if="showPlusPremium">
            <PlanOverviewHome 
              :badges="[
                // {class: 'premium', image: 'premium.svg', name: $t('plans.overview.plus_premium.badge_premium')},
                // {class: 'plus', image: 'plus.svg', name: $t('plans.overview.plus_premium.badge_plus')}
              ]"
              :title="$t('plans.overview.plus_premium.title')"
              :description="$t('plans.overview.plus_premium.description')"
              :features="[
                {name: $t('plans.overview.plus_premium.feature1')},
                // {icon: 'pi-check-circle', name: $t('plans.overview.plus_premium.feature2')},
                // {icon: 'pi-check-circle', name: $t('plans.overview.plus_premium.feature3')},
                // {icon: 'pi-check-circle', name: $t('plans.overview.plus_premium.feature4')}
              ]"
              :button="$t('plans.overview.plus_premium.button')" 
              @click:button="handlePlusPremiumButtonClick"/>
          </div>

          <!-- GELATERIA -->
          <div class="p-col-12 p-lg-6 p-xl-12 p-xxl-6" v-if="showGelateria">
            <PlanOverviewHome 
              :badges="[
                {class: 'gelateria', image: 'gelateria.svg', name: $t('plans.overview.gelateria.badge_gelateria')}
              ]"
              :title="$t('plans.overview.gelateria.title')"
              :description="$t('plans.overview.gelateria.description')"
              :features="[
                {icon: 'pi-check-circle', name: $t('plans.overview.gelateria.feature1')},
                {icon: 'pi-check-circle', name: $t('plans.overview.gelateria.feature2')},
                {icon: 'pi-check-circle', name: $t('plans.overview.gelateria.feature3')}
              ]"
              :disclaimer="$t('plans.overview.gelateria.disclaimer')"
              :button="$t('plans.overview.gelateria.button')" 
              @click:button="handleGelateriaButtonClick"/>
          </div>

        </div>
      </div>
    </div>

    <h3 class="p-mb-4">{{ $t('admin.plans.subtitle') }}</h3>

    <div class="irinox-form" >
      <div class="search-bar" style="float: right">
        <i class="pi pi-search" />
        <InputText v-model="filters['global'].value" :placeholder="$t('admin.appliances.search_short')" style="width:70px" :aria-label="$t('admin.appliances.search_short')"/>
        <i class="pi pi-times" @click="function() {filters['global'].value=null}"/>
      </div>
      <div class="p-formgroup-inline p-ai-center">
        <div class="p-field">
          <Dropdown id="modelNumber" v-model="filters['modelNumber'].value" :options="modelNumbers" optionLabel="name" optionValue="code"
            :placeholder="$t('admin.appliances.filter_model_ph')" />
        </div>
        <div class="p-field">
          <Dropdown id="laboratory" v-model="filters['laboratory'].value" :options="laboratories" optionLabel="name" optionValue="code"
            :placeholder="$t('admin.appliances.filter_lab_ph')" />
        </div>
        <div class="p-field">
          <ToggleButton v-model="statusButton" :onLabel="$t('admin.appliances.filter_online')" :offLabel="$t('admin.appliances.filter_online')"  :aria-label="$t('common.online')+'/'+$t('common.offline')" />      
        </div>
      </div>
    </div>
    
    <DataTable :value="appliances" stripedRows 
      v-model:filters="filters" responsiveLayout="stack" dataKey="serialNumber" 
       breakpoint="900px" sortField="serialNumber" :sortOrder="1"
      :rowClass="getRowClass" @row-click="handleRowClick">
      <template #empty>
          {{$t('admin.appliances.empty_list')}}
      </template>
      <template #loading>
          {{$t('admin.appliances.loading_list')}}
      </template>
      <Column field="serialNumber" :header="$t('admin.appliances.col_serialnumber')" ></Column>
      <Column field="modelNumber" filterField="modelNumber" :header="$t('admin.appliances.col_model')" >
        <template #body="slotProps">
          <div>
            {{$functions.fixModelName(slotProps.data.modelNumber)}} 
            <img v-if="slotProps.data.online" src="assets/irinox/icons/status_online_small.svg" class="p-pl-1" :alt="$t('common.online')"/>
            <img v-else src="assets/irinox/icons/status_offline_small.svg" class="p-pl-1"  :alt="$t('common.offline')"/>
          </div>
        </template>
      </Column>
      <Column field="name" :header="$t('admin.appliances.col_name')" >
        <template #body="slotProps">
          <div>
            {{slotProps.data.name ?? slotProps.data.modelNumber}}
          </div>
        </template>
      </Column>
      <Column field="labName" filterField="laboratory" :header="$t('admin.appliances.col_lab')" ></Column>
      <Column field="plans" :header="$t('admin.plans.col_services')" class="p-p-1">
        <template #body="slotProps">
          <div>
            <div v-if="hasPlanActive(slotProps.data, 'PLUS')" class="plan-badge">
              <div class="plan-plus">
                <img src="/assets/irinox/icons/plans/plus.svg" :alt="$t('admin.plans.table_badge_active_plus')" />
              </div>
              <span>{{ getPlanExpireDate(slotProps.data, 'PLUS') }}</span>
            </div>
            <div v-else-if="hasPlanActive(slotProps.data, 'PREMIUM')" class="plan-badge">
              <div class="plan-premium">
                <img src="/assets/irinox/icons/plans/premium.svg" :alt="$t('admin.plans.table_badge_active_premium')" />
              </div>
              <span>{{ getPlanExpireDate(slotProps.data, 'PREMIUM') }}</span>
            </div>
            <div v-else-if="hasPlanActivable(slotProps.data, 'PLUS') && !hasPlanActivable(slotProps.data, 'PREMIUM') && !hasPlanActive(slotProps.data, 'PREMIUM')" class="plan-badge">
              <div class="plan-plus small">{{$t('admin.plans.table_badge_activable_plus')}}</div>
            </div>
            <div v-else-if="hasPlanActivable(slotProps.data, 'PREMIUM') && !hasPlanActive(slotProps.data, 'PLUS')" class="plan-badge">
              <div class="plan-premium small">{{$t('admin.plans.table_badge_activable_premium')}}</div>
            </div>
            <div v-if="hasPlanActive(slotProps.data, 'GELATERIA')" class="plan-badge">
              <div class="plan-gelateria">
                <img src="/assets/irinox/icons/plans/gelateria.svg" :alt="$t('admin.plans.table_badge_active_gelateria')" />
              </div>
              <span>{{ getPlanExpireDate(slotProps.data, 'GELATERIA') }}</span>
            </div>
            <div v-else-if="hasPlanActivable(slotProps.data, 'GELATERIA')" class="plan-badge">
              <div class="plan-gelateria small">{{$t('admin.plans.table_badge_activable_gelateria')}}</div>
            </div>
          </div>
        </template>
      </Column>
      <Column class="irinox-table-buttons p-text-right">
        <template #header>
          <span class="sr-only">{{$t('common.select')}}</span>
        </template>
        <template #body="slotProps">
          <div v-if="hasPlans(slotProps.data)"  class="button-body">
            <i class="pi pi-chevron-right" @click="sidebar.appliance = slotProps.data; sidebar.show = true;" />
          </div>
        </template>
      </Column>
    </DataTable>


    <!-- <div>
      <pre>{{ plans }}</pre>
      <pre>{{ appliances }}</pre>
    </div> -->


    <IrinoxDialogDark :visible="dialog.error" mode="error" :message="$t('details.dialog_error')" :life="3000"
      @update:visible="dialog.error = $event" />

    <PlanComparePlusPremium 
      :visible="dialog.comparePlusPremium" 
      @update:visible="dialog.comparePlusPremium = $event"
      @click:activate="handlePlusPremiumActivate"
      :plans="plans"
      :plansMapping="plansMapping" />

    <PlanSelectDeviceDialog
      :visible="dialog.selectDevice"
      @update:visible="dialog.selectDevice = $event"
      :plans="plans"
      :appliances="appliances"
      :selectedPlanCode="selectedPlanCode"
      :plansMapping="plansMapping" />

    <PlansSidebar v-model:showSidebar="sidebar.show" :plans="plans" :plansMapping="plansMapping" :appliance="sidebar.appliance" />

  </div>
</template>

<script>
import ApplianceService from '../../service/ApplianceService';
import IrinoxDialogDark from '../IrinoxDialogDark.vue';
import PlanOverviewHome from './plans/PlanOverviewHome.vue';
import PlanComparePlusPremium from './plans/PlanComparePlusPremium.vue';
import PlanSelectDeviceDialog from './plans/PlanSelectDeviceDialog.vue';
import PlansSidebar from './plans/PlansSidebar.vue';
import {FilterMatchMode} from 'primevue/api';
import { mapGetters } from 'vuex';
import plansMapping from "../../../public/json/plans_mapping.json";

export default {
  components: {
    IrinoxDialogDark,
    PlanOverviewHome,
    PlanComparePlusPremium,
    PlanSelectDeviceDialog,
    PlansSidebar
  },
  setup() {
    // nessuna logica per ora
  },
  props: {
    plans: {
      type: Array,
      default: () => []
    },
  },
  data() {
    return {
      filters: {
          'global': {value: null, matchMode: FilterMatchMode.CONTAINS},
          'modelNumber': {value: null, matchMode: FilterMatchMode.EQUALS},
          'laboratory': {value: null, matchMode: FilterMatchMode.EQUALS},
          'online': {value: null, matchMode: FilterMatchMode.EQUALS}
      },
      appliances: [],
      modelNumbers: [],
      laboratories: [],
      statusButton: false,
      dialog: {
        error: false,
        comparePlusPremium: false,
        selectDevice: false,
      },
      selectedPlanCode: null,
      plansMapping: plansMapping,
      sidebar: {
        show: false,
        appliance: null
      }
    };
  },
  computed: {
    ...mapGetters({
      personaltoken: 'userdata/personaltoken',
    }),
    showPlusPremium() {
      return this.plans?.some(plan => plan.code === 'PLUS' || plan.code === 'PREMIUM');
    },
    showGelateria() {
      return this.plans?.some(plan => plan.code === 'GELATERIA');
    },
  },
  watch: {
  },
  created() {
    this.applianceService = new ApplianceService();
  },
  mounted() {
    this.getAppliances();
  },
  methods: {
    getAppliances() {
      this.applianceService.getAppliances(this.personaltoken)
        .then((data) => {
          // if (data.code == "200") {
          //   this.appliances = data.data;
          // } else {
          //   console.error(JSON.stringify(data));
          //   this.appliances = [];
          //   this.dialog.error = true;
          // }
          this.appliances = data;
          this.loadFilters();
        })
        .catch(error => {
          console.error("ERROR " + error);
          this.appliances = [];
          this.dialog.error = true;
        });
    },
    loadFilters() {
        this.modelNumbers = [];
        this.laboratories = [];
        for (let i = 0; i < this.appliances.length; i++) {
          this.modelNumbers.push({ 
            code: this.appliances[i].modelNumber,
            name: this.$functions.fixModelName(this.appliances[i].modelNumber)
          });
          if(this.appliances[i].labName) {
            this.laboratories.push({ 
              code: this.appliances[i].labName,
              name: this.appliances[i].labName 
            });
          }
        }
        this.modelNumbers = this.$functions.uniqByKeepFirst(this.modelNumbers, item => item.code).sort((a,b) => this.$functions.compareStrings(a,b,'name'));
        this.modelNumbers.unshift({ 
          code: null,
          name: this.$t('admin.appliances.filter_remove') 
        });
        this.laboratories = this.$functions.uniqByKeepFirst(this.laboratories, item => item.code).sort((a,b) => this.$functions.compareStrings(a,b,'name'));
        this.laboratories.unshift({ 
          code: null,
          name: this.$t('admin.appliances.filter_remove') 
        })
      },
      statusButtonToggle() {
        if(this.statusButton) {
          this.filters['online'].value = true;
        } else {
          this.filters['online'].value = null;
        }
      },
      hasPlanActive(appliance, planCode) {
        return appliance?.plans?.some(plan => plan.code === planCode && plan.startDate !=null);
      },
      hasPlanActivable(appliance, planCode) {
        return appliance?.plans?.some(plan => plan.code === planCode && plan.startDate == null);
      },
      hasPlans(appliance) {
        return appliance?.plans?.length > 0;
      },
      getPlanExpireDate(appliance, planCode) {
        const plan = appliance?.plans?.find(plan => plan.code === planCode);
        return plan ? this.$d(plan.endDate) : null;
      },
      handlePlusPremiumButtonClick() {
        this.dialog.comparePlusPremium = true;
      },
      handlePlusPremiumActivate(planCode) {
        this.selectedPlanCode = planCode;
        this.dialog.selectDevice = true;
      },
      handleGelateriaButtonClick() {
        this.$window.open(this.plansMapping[this.$i18n.locale=='en-US'? 'us' : this.$i18n.locale]['gelateria'].info, '_blank');
      },
      getRowClass(data) {
        return this.hasPlans(data) ? 'row-clickable' : null;
      },
      handleRowClick(event) {
        if (this.hasPlans(event.data)) {
          this.sidebar.appliance = event.data;
          this.sidebar.show = true;
        }
      },
  },
}
</script>

<style scoped>
:deep(.row-clickable) {
  cursor: pointer;
}
</style>